import { useState, useEffect, useCallback, useRef } from "react";
import { batchResolveUsers } from "../../../api/user";

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

// ── Hook: resuelve ObjectIds a nombres legibles con cache ─────────────────────
export function useUserNames(conversations, messages) {
  const [userNames, setUserNames] = useState({});
  const pendingRef = useRef(new Set());

  useEffect(() => {
    const ids = new Set();
    for (const conv of conversations) {
      if (conv.user_id) ids.add(conv.user_id);
      if (conv.assigned_to) ids.add(conv.assigned_to);
      // assigned_by aún no existe en prod (backend en curso): si llega, se
      // resuelve para tooltips "Asignada por…"; si falta, no agrega nada.
      if (conv.assigned_by) ids.add(conv.assigned_by);
      // Historial de casos (popover): quién atendió y quién cerró cada caso
      // sellado. 'system' no es un usuario (cierres automáticos y masivos).
      for (const entry of conv.case_history || []) {
        for (const id of [entry?.resolved_by, entry?.assigned_to, entry?.assigned_by]) {
          if (id && id !== "system") ids.add(id);
        }
      }
    }
    for (const msg of messages) {
      if (msg.sender_id) ids.add(msg.sender_id);
      // Mensajes system de asignación (metadata.kind === "assignment"): sus ids
      // viajan en metadata, no en sender_id.
      if (msg.metadata?.assigned_to) ids.add(msg.metadata.assigned_to);
      if (msg.metadata?.assigned_by) ids.add(msg.metadata.assigned_by);
      // Marca de cierre de caso (kind "case_closed"): quién cerró viaja en
      // metadata.resolved_by; el sender_id de la marca es 'system'.
      if (msg.metadata?.resolved_by && msg.metadata.resolved_by !== "system") {
        ids.add(msg.metadata.resolved_by);
      }
      // Nota privada (kind "private_note"): autor y @mencionados, para pintar
      // el nombre del autor y resaltar las menciones en el hilo. Solo ObjectIds:
      // el actor de una regla ("automation:<id>") no es un usuario.
      if (msg.metadata?.kind === "private_note") {
        const candidates = [
          msg.metadata.author_id,
          ...(Array.isArray(msg.metadata.mentions) ? msg.metadata.mentions : []),
        ];
        for (const id of candidates) {
          if (typeof id === "string" && OBJECT_ID_RE.test(id)) ids.add(id);
        }
      }
    }

    const toResolve = [...ids].filter(
      (id) => !userNames[id] && !pendingRef.current.has(id)
    );
    if (toResolve.length === 0) return;

    toResolve.forEach((id) => pendingRef.current.add(id));
    batchResolveUsers(toResolve).then((result) => {
      toResolve.forEach((id) => pendingRef.current.delete(id));
      if (Object.keys(result).length > 0) {
        setUserNames((prev) => ({ ...prev, ...result }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, messages]);

  const getDisplayName = useCallback(
    (id, withRole = false) => {
      if (!id) return "";
      const entry = userNames[id];
      if (!entry) return id;
      const { displayName, role } = entry;
      if (withRole && role) return `${displayName} (${role})`;
      return displayName;
    },
    [userNames]
  );

  return { userNames, getDisplayName };
}
